import { Link } from "react-router-dom";
import { Copy, Disc3, Pencil } from "lucide-react";
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { formatDate } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { IssuePills, Pill, ReleasedPill, SectionTag, SocietyDots, SplitBar, WRITER_COLOURS, writerLine } from "@/components/Music";
import { cn } from "@/lib/utils";

export function artistLine(t) {
  return [t.artist || "Unknown artist", t.featured && `ft. ${t.featured}`].filter(Boolean).join(" ");
}

function Info({ label, children, mono }) {
  return (
    <div className="min-w-0">
      <div className="eyebrow text-[10px]">{label}</div>
      <div className={cn("mt-1 text-[13px] text-ink break-words", mono && "font-mono text-[12.5px]")}>{children || <span className="text-muted2">—</span>}</div>
    </div>
  );
}

/** Quick look at a track, opened from a list row or card. */
export function TrackDrawer({ track, open, onOpenChange, canEdit, onDuplicate }) {
  const { settings, societyLabel } = useSettings();
  const t = track;
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-[500px] p-0 bg-card flex flex-col gap-0">
        {t && (
          <>
            <div className="p-6 pb-5 border-b border-line flex gap-4 items-start pr-12">
              <div className="w-12 h-12 rounded-2xl bg-brand flex items-center justify-center shrink-0">
                <Disc3 className="w-6 h-6 text-white" />
              </div>
              <div className="min-w-0">
                <SheetTitle className="font-display text-[22px] leading-tight font-semibold text-ink">{t.title}</SheetTitle>
                <SheetDescription className="text-[13px] text-muted2 mt-1">{artistLine(t)}</SheetDescription>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  <ReleasedPill value={t.released} />
                  <Pill tone="brand">{t.stage === "Released" ? "Out" : `Stage: ${t.stage}`}</Pill>
                </div>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {t.issues.length > 0 && (
                <div className="rounded-xl border border-line p-3.5">
                  <div className="eyebrow mb-2">To fix</div>
                  <IssuePills issues={t.issues} max={8} />
                </div>
              )}
              <section className="space-y-3">
                <SectionTag k="release" />
                <div className="grid grid-cols-2 gap-4">
                  <Info label="Release date">{formatDate(t.release_date)}</Info>
                  <Info label="Original release">{t.original_release_date && formatDate(t.original_release_date)}</Info>
                  <Info label="Label">{t.label}</Info>
                  <Info label="Riddim / album">{t.riddim}</Info>
                  <Info label="Length" mono>
                    {t.length}
                  </Info>
                </div>
              </section>
              <section className="space-y-3">
                <SectionTag k="codes" />
                <div className="grid grid-cols-3 gap-2.5">
                  {[
                    ["ISRC", t.isrc],
                    ["UPC", t.upc],
                    ["CAT", t.cat],
                  ].map(([l, v]) => (
                    <div key={l} className="rounded-xl bg-surface px-3 py-2.5 min-w-0">
                      <div className="eyebrow text-[10px]">{l}</div>
                      <div className={cn("font-mono text-[12px] mt-1 break-all", !v && "text-stock-out")}>{v || "Missing"}</div>
                    </div>
                  ))}
                </div>
              </section>
              <section className="space-y-3">
                <div className="flex items-center justify-between">
                  <SectionTag k="publishing" />
                  <span className={cn("font-mono text-[12px] font-semibold", Math.abs(t.split_total - 100) < 0.01 ? "text-stock-in" : "text-stock-out")}>
                    Total {t.split_total}%
                  </span>
                </div>
                <SplitBar writers={t.writers} tall />
                <div>
                  {t.writers.map((w, i) => (
                    <div key={i} className="flex items-center gap-2.5 py-2 border-b border-line/70 last:border-0">
                      <span className={cn("w-2.5 h-2.5 rounded-[3px]", WRITER_COLOURS[i] || WRITER_COLOURS[3])} />
                      <Link to={`/writers?open=${w.writer_id}`} className="flex-1 text-[13px] text-ink hover:text-brand truncate">
                        {w.name}
                      </Link>
                      <span className="text-[11.5px] text-muted2">{w.pro || "—"}</span>
                      <span className={cn("font-mono text-[11.5px] w-[92px] text-right", !w.ipi && "text-gold")}>{w.ipi || "no IPI"}</span>
                      <span className="font-mono text-[12.5px] w-10 text-right">{w.share == null ? "—" : `${w.share}%`}</span>
                    </div>
                  ))}
                  {!t.writers.length && <p className="text-[13px] text-stock-out">No writers yet.</p>}
                </div>
              </section>
              <section className="space-y-3">
                <SectionTag k="mechanical" />
                <div className="grid grid-cols-3 gap-2">
                  {settings.societies.map((s) => {
                    const r = t.regs[s.key];
                    return (
                      <div key={s.key} className={cn("rounded-xl px-3 py-2.5", r.done ? "bg-sec-mech text-white" : "bg-surface text-muted2")}>
                        <div className="font-mono text-[12px] font-semibold">{societyLabel(s.key)}</div>
                        <div className="text-[11.5px] mt-0.5" title={r.status === "rejected" && r.note ? r.note : undefined}>
                          {r.done ? (r.date ? formatDate(r.date) : "Registered") : { submitted: "Submitted", rejected: "Rejected", na: "Doesn't apply" }[r.status] || "Not yet"}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
              <section className="space-y-3">
                <SectionTag k="dist" />
                <div className="grid grid-cols-2 gap-4">
                  <Info label="Distributor">{t.distributor}</Info>
                  <Info label="PRO / IPI on sheet">{[t.pro, t.ipi].filter(Boolean).join(" · ")}</Info>
                </div>
              </section>
              {t.notes && (
                <section>
                  <div className="eyebrow mb-1">Notes</div>
                  <p className="text-[13px] whitespace-pre-line">{t.notes}</p>
                </section>
              )}
            </div>
            {canEdit && (
              <div className="p-4 border-t border-line flex justify-end gap-2">
                <Button variant="outline" onClick={() => onDuplicate?.(t)}>
                  <Copy className="w-4 h-4 mr-2" /> Duplicate
                </Button>
                <Button asChild className="bg-brand hover:bg-brand-hover">
                  <Link to={`/catalog/${t.track_id}`}>
                    <Pencil className="w-4 h-4 mr-2" /> Edit track
                  </Link>
                </Button>
              </div>
            )}
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

export function TrackCard({ t, onOpen, selected, onSelect, labels }) {
  return (
    <div className={cn("panel p-4 flex flex-col gap-3 hover:border-brand-ring transition-colors relative", selected && "ring-2 ring-brand-ring")}>
      {onSelect && (
        <input
          type="checkbox"
          aria-label={`Select ${t.title}`}
          checked={!!selected}
          onChange={() => onSelect(t)}
          className="absolute top-4 right-4 w-4 h-4 accent-[hsl(var(--brand))]"
        />
      )}
      <button onClick={() => onOpen(t)} className="text-left flex gap-3 items-start pr-6">
        <div className="w-11 h-11 rounded-xl bg-brand-light flex items-center justify-center shrink-0">
          <Disc3 className="w-5 h-5 text-brand" />
        </div>
        <div className="min-w-0">
          <div className="font-display text-[16px] font-semibold leading-snug text-ink truncate">{t.title}</div>
          <div className="text-[12.5px] text-muted2 truncate">{artistLine(t)}</div>
        </div>
      </button>
      <div className="flex flex-wrap items-center gap-1.5">
        <ReleasedPill value={t.released} />
        {t.release_date && <span className="text-[12px] text-muted2">{formatDate(t.release_date)}</span>}
        {t.distributor && <Pill tone="dist">{t.distributor}</Pill>}
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div className="rounded-lg bg-surface px-2.5 py-1.5 min-w-0">
          <div className="eyebrow text-[9.5px]">ISRC</div>
          <div className="font-mono text-[11.5px] truncate">{t.isrc || <span className="text-stock-out">Missing</span>}</div>
        </div>
        <div className="rounded-lg bg-surface px-2.5 py-1.5 min-w-0">
          <div className="eyebrow text-[9.5px]">UPC</div>
          <div className="font-mono text-[11.5px] truncate">{t.upc || <span className="text-stock-out">Missing</span>}</div>
        </div>
      </div>
      <div>
        <SplitBar writers={t.writers} />
        <div className="text-[12px] text-muted2 mt-1.5 truncate">{writerLine(t.writers)}</div>
      </div>
      <div className="flex items-center justify-between gap-2 pt-2 border-t border-line mt-auto">
        <SocietyDots regs={t.regs} size="sm" labels={labels} />
        <IssuePills issues={t.issues} max={1} />
      </div>
    </div>
  );
}
